import { Box, Spinner, Text, Field } from "@chakra-ui/react";
import { useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import { Heading, CheckboxGroup, Checkbox, ErrorMessage } from "@digital-services-gard-as/anchor-react";
import { useCallback } from "react";
import { createInvoiceAddressText } from "@/utils/addressUtils";
import { MemberDetailsDto, InvoiceFormData } from "@/types";

type InvoiceConfirmationProps = {
    invoiceRecipient: MemberDetailsDto | undefined;
    isLoadingInvoiceRecipient?: boolean;
    claimRef?: string;
};

export const InvoiceConfirmation = ({
    invoiceRecipient,
    isLoadingInvoiceRecipient,
    claimRef
}: InvoiceConfirmationProps) => {
    const { t } = useTranslation(["invoices"]);
    const {
        watch,
        setValue,
        formState: { errors }
    } = useFormContext<InvoiceFormData>();

    const addressConfirmation = watch("addressConfirmation");
    const totalConfirmation = watch("totalConfirmation");
    const personalSensitiveConfirmation = watch("personalSensitiveConfirmation");

    const hasAnyErrors =
        Boolean(errors.addressConfirmation?.message) ||
        Boolean(errors.totalConfirmation?.message) ||
        Boolean(errors.personalSensitiveConfirmation?.message);

    const onAddressChange = useCallback(
        function handleAddressChange(e: React.ChangeEvent<HTMLInputElement>) {
            setValue("addressConfirmation", e.target.checked);
        },
        [setValue]
    );
    const onTotalChange = useCallback(
        function handleTotalChange(e: React.ChangeEvent<HTMLInputElement>) {
            setValue("totalConfirmation", e.target.checked);
        },
        [setValue]
    );
    const onPersonalSensitiveChange = useCallback(
        function handlePersonalSensitiveChange(e: React.ChangeEvent<HTMLInputElement>) {
            setValue("personalSensitiveConfirmation", e.target.checked);
        },
        [setValue]
    );
    return (
        <Box bg="white" p={{ base: "1rem", md: "1.5rem" }} borderRadius="md" boxShadow="xs">
            <Heading size="md" style={{ marginBottom: "0.5rem" }}>
                {t("createInvoice.feesAndExpenses.confirmation.confirmations")}
            </Heading>
            <Field.Root invalid={hasAnyErrors}>
                <CheckboxGroup legend={t("createInvoice.feesAndExpenses.confirmation.guidance")}>
                    <Checkbox
                        value="addressConfirmation"
                        checked={Boolean(addressConfirmation)}
                        onChange={onAddressChange}
                        data-testid="addressConfirmation"
                        size="sm"
                    >
                        <Text fontSize="md">
                            <Trans t={t} i18nKey="createInvoice.feesAndExpenses.confirmation.addressConfirmation" />
                        </Text>
                    </Checkbox>
                    <Box bgColor="anchor.gray.50" p="1rem" w="95%" display="flex" justifySelf="center" my="1rem">
                        {isLoadingInvoiceRecipient ? (
                            <Spinner />
                        ) : (
                            <Text whiteSpace="pre-wrap">
                                {claimRef
                                    ? createInvoiceAddressText(invoiceRecipient)
                                    : t("createInvoice.missingClaim")}
                            </Text>
                        )}
                    </Box>
                    <ErrorMessage size="sm" data-testid="addressConfirmationError">
                        {errors.addressConfirmation?.message}
                    </ErrorMessage>
                    <Checkbox
                        value="totalConfirmation"
                        checked={Boolean(totalConfirmation)}
                        onChange={onTotalChange}
                        data-testid="totalConfirmation"
                        size="sm"
                    >
                        <Trans t={t} i18nKey="createInvoice.feesAndExpenses.confirmation.totalAmountMatch" />
                    </Checkbox>

                    <ErrorMessage size="sm" data-testid="totalConfirmationError">
                        {errors.totalConfirmation?.message}
                    </ErrorMessage>
                    <Checkbox
                        value="personalSensitiveConfirmation"
                        checked={Boolean(personalSensitiveConfirmation)}
                        onChange={onPersonalSensitiveChange}
                        data-testid="personalSensitiveConfirmation"
                        size="sm"
                    >
                        <Trans
                            t={t}
                            i18nKey="createInvoice.feesAndExpenses.confirmation.personalSensitiveConfirmation"
                        />
                    </Checkbox>
                    <ErrorMessage size="sm" data-testid="personalSensitiveConfirmationError">
                        {errors.personalSensitiveConfirmation?.message}
                    </ErrorMessage>
                </CheckboxGroup>
            </Field.Root>
        </Box>
    );
};
